/**
 * Derived locale constants - computed from DEFAULT_RMS_REGION_DATA (rms-data.ts).
 * No import from locale-config.types; types are inferred locally to avoid circular dependency.
 */

import { DEFAULT_LOCALE, LOCALE_FORMAT } from './locale-config.static-constants'
import { DEFAULT_RMS_REGION_DATA } from './rms-data'

/** Default locale in standard format (language-Country), derived from DEFAULT_LOCALE */
export const LOCALE_STANDARD_FORMAT = (() => {
  const [lang, country = ''] = DEFAULT_LOCALE.split('-')
  return `${lang}-${country.toUpperCase()}`
})()

// LOCAL TYPES (inferred from data - do not import from locale-config.types)
// =============================================================================

type Region = (typeof DEFAULT_RMS_REGION_DATA)[number]
type CountryCode = Lowercase<Region['regionCode']>

// =============================================================================
// DERIVED CONSTANTS (Computed from DEFAULT_RMS_REGION_DATA)
// =============================================================================

/** Supported countries array - DERIVED from DEFAULT_RMS_REGION_DATA */
export const SUPPORTED_COUNTRIES = DEFAULT_RMS_REGION_DATA.map(region =>
  region.regionCode.toLowerCase(),
) as readonly CountryCode[]

/** Country currency mappings - DERIVED from DEFAULT_RMS_REGION_DATA */
export const COUNTRY_CURRENCIES: Record<CountryCode, Region['defaultCurrencyIsoCode']> = Object.fromEntries(
  DEFAULT_RMS_REGION_DATA.map(region => [region.regionCode.toLowerCase(), region.defaultCurrencyIsoCode]),
) as Record<CountryCode, Region['defaultCurrencyIsoCode']>

/** Currency ISO code → regionId - DERIVED from DEFAULT_RMS_REGION_DATA (region↔currency is 1:1). */
export const CURRENCY_TO_REGION_ID: Record<string, number> = Object.fromEntries(
  DEFAULT_RMS_REGION_DATA.map(region => [region.defaultCurrencyIsoCode, region.regionId]),
)

/** Country language mappings - DERIVED from DEFAULT_RMS_REGION_DATA
 * Extracts language codes (e.g., 'en', 'fr') from locale strings (e.g., 'en-CA', 'fr-CA')
 */
export const COUNTRY_LANGUAGES: Record<CountryCode, readonly string[]> = Object.fromEntries(
  DEFAULT_RMS_REGION_DATA.map(region => [
    region.regionCode.toLowerCase(),
    region.supportedLanguagesIsoCodes.map(l => {
      const parts = l.toLowerCase().split('-')
      return parts[0] ?? ''
    }) as readonly string[],
  ]),
) as unknown as Record<CountryCode, readonly string[]>

/** Default country - first entry in SUPPORTED_COUNTRIES */
const firstCountry = SUPPORTED_COUNTRIES[0]
if (!firstCountry) {
  throw new Error('SUPPORTED_COUNTRIES must have at least one entry')
}
export const DEFAULT_COUNTRY: (typeof SUPPORTED_COUNTRIES)[number] = firstCountry

/** Formats a locale string from country and language */
export function formatLocale(country: (typeof SUPPORTED_COUNTRIES)[number], language: string): string {
  const countryStr = String(country)
  const languageStr = String(language)
  /* eslint-disable i18next/no-literal-string -- format uses template placeholders from LOCALE_FORMAT */
  const result = LOCALE_FORMAT.replace('{{country}}', countryStr).replace('{{language}}', languageStr)
  /* eslint-enable i18next/no-literal-string */
  return result
}

/** Fallback locale (computed from default country and language) */
const defaultCountryLanguages = COUNTRY_LANGUAGES[DEFAULT_COUNTRY]
if (defaultCountryLanguages.length === 0) {
  throw new Error(`COUNTRY_LANGUAGES[${DEFAULT_COUNTRY}] must contain at least one language`)
}
const defaultLanguage = defaultCountryLanguages[0]
if (!defaultLanguage) {
  throw new Error(`COUNTRY_LANGUAGES[${DEFAULT_COUNTRY}] must have at least one language`)
}
export const FALLBACK_LOCALE = formatLocale(DEFAULT_COUNTRY, defaultLanguage)

// =============================================================================
// COUNTRY CONFIGURATION
// =============================================================================

/** regionId → displayName for RMS regions (e.g. 101 → "United States"). Used for review "Reviewed in {country}" from API regionId. */
export const REGION_ID_TO_DISPLAY_NAME: Record<number, string> = Object.fromEntries(
  DEFAULT_RMS_REGION_DATA.map(region => [region.regionId, region.displayName]),
) as Record<number, string>

/**
 * country → regionId for RMS regions (e.g. `gb` → 103). The inverse of the lookup
 * `getRegionIdFromLocale` performs against a live `regionDetails` payload — this static form lets
 * client-side code map a locale to its region id without holding regionDetails (which is only
 * available via pageProps / the locale context). Used by the SAND region-header resolver to derive
 * a region from the server-rendered locale when the region cookie is absent.
 */
export const COUNTRY_REGION_IDS: Record<CountryCode, number> = Object.fromEntries(
  DEFAULT_RMS_REGION_DATA.map(region => [region.regionCode.toLowerCase(), region.regionId]),
) as Record<CountryCode, number>

/** Country configuration with flag - DERIVED from DEFAULT_RMS_REGION_DATA */
export const COUNTRY_CONFIG: Record<(typeof SUPPORTED_COUNTRIES)[number], { flag: string }> = Object.fromEntries(
  DEFAULT_RMS_REGION_DATA.map(region => [region.regionCode.toLowerCase(), { flag: region.flag }]),
) as Record<(typeof SUPPORTED_COUNTRIES)[number], { flag: string }>

// =============================================================================
// SUPPORTED LOCALES (lowercase) – DERIVED from DEFAULT_RMS_REGION_DATA
// =============================================================================

/** All supported locale strings in lowercase (e.g. en-us, en-ca, fr-ca) – for supportedLocales list */
export const SUPPORTED_LOCALES: readonly string[] = DEFAULT_RMS_REGION_DATA.flatMap(region =>
  region.supportedLanguagesIsoCodes.map(locale => locale.toLowerCase()),
)

// =============================================================================
// NORMALISED LOCALE (consistent casing for config key lookup)
// =============================================================================

/**
 * Returns locale in normalised form (language lowercase, country uppercase).
 * Consistent for any input casing: en-ca, en-CA, EN-CA, EN-ca → 'en-CA'.
 * Use for config key lookup (e.g. DEFAULT_LOCALE_CONFIG_MAP) where keys are en-US, en-CA, fr-CA.
 */
export function toNormalisedLocale(locale: string): string {
  const [lang = '', country = ''] = locale.split('-')
  return `${lang.toLowerCase()}-${country.toUpperCase()}`
}
